/** @typedef {'product' | 'cart'} BnplPageType */

export const BNPL_PAGE_TYPE = Object.freeze({
  PRODUCT: 'product',
  CART: 'cart',
});

export const ZIP_WIDGET_ASSET = Object.freeze({
  PRODUCT: 'productwidget',
  CART: 'cartwidget',
});

/** Matches theme breakpoints.lg (992px) — mobile base, desktop lg+. */
export const BNPL_BREAKPOINT_LG = '992px';

const bnplSize = Object.freeze({base: '12px', lg: '13px'});

const BNPL_PROVIDER_ROW_CLASS = 'bnpl-messaging__provider-row';

/** @param {string} providerKey e.g. `afterpay`, `zip` */
export const buildBnplMessagingProviderRowClassName = (providerKey) => `${BNPL_PROVIDER_ROW_CLASS} bnpl-messaging__${providerKey}`;

/** PDP BNPL row — 14px; theme uses `grayAlpha.90`. */
export const BNPL_PDP_MESSAGING_FONT_SIZE = '14px';

/** Matches theme `grayAlpha.90` — required as literal for Afterpay shadow DOM injection. */
export const BNPL_PDP_TEXT_COLOR = 'rgba(27, 26, 24, 0.9)';

/** Shared logo / info-icon sizing for Afterpay + Zip widgets (desktop + mobile). */
export const BNPL_WIDGET_STYLE = Object.freeze({
  AFTERPAY_LOGO_BADGE_WIDTH: '70px',
  AFTERPAY_LOGO_BADGE_INCLUDE_TEXT_WIDTH: '82px',
  FONT_SIZE: bnplSize,
  INFO_ICON_SIZE: bnplSize,
  LOGO_HEIGHT: Object.freeze({base: '24px', lg: '24px'}),
});

/** Afterpay shadow DOM ignores @media — pass viewport-derived sizes from matchMedia instead. */
export const buildAfterpayShadowStyles = (isDesktop = false, useCartTypography = false) => {
  const logoHeight = isDesktop ? BNPL_WIDGET_STYLE.LOGO_HEIGHT.lg : BNPL_WIDGET_STYLE.LOGO_HEIGHT.base;
  const infoIconSize = useCartTypography ?
    (isDesktop ? BNPL_WIDGET_STYLE.INFO_ICON_SIZE.lg : BNPL_WIDGET_STYLE.INFO_ICON_SIZE.base) :
    BNPL_PDP_MESSAGING_FONT_SIZE;
  const messagingFontSize = useCartTypography ?
    (isDesktop ? BNPL_WIDGET_STYLE.FONT_SIZE.lg : BNPL_WIDGET_STYLE.FONT_SIZE.base) :
    BNPL_PDP_MESSAGING_FONT_SIZE;
  const badgeWidthToken = useCartTypography ?
    BNPL_WIDGET_STYLE.AFTERPAY_LOGO_BADGE_INCLUDE_TEXT_WIDTH :
    BNPL_WIDGET_STYLE.AFTERPAY_LOGO_BADGE_WIDTH;
  const logoBadgeWithTextWidth = `var(--logo-badge-width, ${badgeWidthToken})`;
  const logoHeightRatio =
    Number.parseFloat(logoHeight) / Number.parseFloat(BNPL_WIDGET_STYLE.AFTERPAY_LOGO_BADGE_INCLUDE_TEXT_WIDTH);

  const messagingColorRules = useCartTypography ?
    '' :
    `
    :host {
      color: ${BNPL_PDP_TEXT_COLOR} !important;
      display: block !important;
      --messaging-font-size: ${messagingFontSize} !important;
      --messaging-line-height: 1.4 !important;
      --messaging-margin-block-end: 0 !important;
      --messaging-margin-block-start: 0 !important;
      width: 100% !important;
    }

    p,
    .afterpay-paragraph {
      display: block !important;
      font-size: ${messagingFontSize} !important;
      line-height: var(--messaging-line-height, 1.4) !important;
      margin: 0 !important;
      white-space: normal !important;
    }

    strong,
    b,
    .afterpay-text2 {
      display: inline !important;
      font-weight: 400 !important;
      vertical-align: middle !important;
    }

    .afterpay-paragraph button.afterpay-logo {
      display: inline-block !important;
      vertical-align: middle !important;
    }

    .afterpay-paragraph #logo-link {
      display: inline-block !important;
      vertical-align: middle !important;
    }
  `;

  const logoBadgeRules = useCartTypography ?
    `
    .afterpay-logo.brand-afterpay.logo-cashappafterpay {
      max-width: ${logoBadgeWithTextWidth} !important;
      height: calc(${logoBadgeWithTextWidth} * ${logoHeightRatio}) !important;
    }
    .afterpay-logo.brand-afterpay.logo-cashappafterpay svg {
      max-width: 100% !important;
    }
    ` :
    `
    button.afterpay-logo.brand-afterpay.logo-cashappafterpay,
    button.afterpay-logo.brand-afterpay.logo-cashappafterpay svg {
      height: calc(20 / 82 * ${logoBadgeWithTextWidth}) !important;
      max-width: ${logoBadgeWithTextWidth} !important;
      vertical-align: middle !important;
      width: ${logoBadgeWithTextWidth} !important;
    }
    button.afterpay-logo.brand-afterpay.logo-cashappafterpay svg {
      display: block !important;
    }
    `;

  const logoLinkRules = useCartTypography ?
    `
    #logo-link {
      display: inline-block !important;
      font-size: ${infoIconSize} !important;
      height: ${logoHeight} !important;
      line-height: 1 !important;
      min-height: ${logoHeight} !important;
      min-width: ${infoIconSize} !important;
      vertical-align: middle !important;
    }

    #logo-link svg,
    button.afterpay-logo svg {
      height: ${logoHeight} !important;
      width: auto !important;
    }
    ` :
    `
    #logo-link {
      display: inline-block !important;
      font-size: inherit !important;
      height: auto !important;
      line-height: 1 !important;
      min-height: 0 !important;
      min-width: 0 !important;
      padding: 0 !important;
      vertical-align: middle !important;
    }

    #logo-link svg {
      height: ${infoIconSize} !important;
      vertical-align: middle !important;
      width: ${infoIconSize} !important;
    }
    `;

  return `
    ${messagingColorRules}
    ${logoLinkRules}

    #info-link {
      display: inline-block !important;
      font-size: ${infoIconSize} !important;
      line-height: 1 !important;
      vertical-align: middle !important;
    }

    ${logoBadgeRules}
  `;
};

export const BNPL_DEFAULTS = Object.freeze({
  AFTERPAY_SCRIPT_URL: 'https://js.squarecdn.com/square-marketplace.js',
  AFTERPAY_MIN_AMOUNT: '1',
  AFTERPAY_MAX_AMOUNT: '4000.00',
  AFTERPAY_PLATFORM: 'SFCC',
  ZIP_SCRIPT_URL: 'https://static.zip.co/lib/js/zm-widget-js/dist/zip-widget.min.js',
  ZIP_ENVIRONMENT: 'production',
});

export const BNPL_PREFERENCE_KEYS = Object.freeze({
  AFTERPAY_ENABLED: 'AFTERPAY_ENABLED',
  AFTERPAY_MPID: 'AFTERPAY_MPID',
  AFTERPAY_SCRIPT_URL: 'AFTERPAY_SCRIPT_URL',
  AFTERPAY_MIN_AMOUNT: 'AFTERPAY_MIN_AMOUNT',
  AFTERPAY_MAX_AMOUNT: 'AFTERPAY_MAX_AMOUNT',
  AFTERPAY_PLATFORM: 'AFTERPAY_PLATFORM',
  ZIP_ENABLED: 'ZIP_ENABLED',
  ZIP_MERCHANT_KEY: 'ZIP_MERCHANT_KEY',
  ZIP_ENVIRONMENT: 'ZIP_ENVIRONMENT',
  ZIP_SCRIPT_URL: 'ZIP_SCRIPT_URL',
});
